import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { gatewayLabel, type GatewayEntry } from '../managers/gateway-entry';
import { entryConnState } from '../managers/conn-state';
import type { BackendClient } from '../bridge/backend-client';
import type { ConnectionStatus, DiscoveryStatus } from '../../core/domain/controller';
import { prefetchFirstPage } from './symbolGrid/pagePrefetch';
import { t } from '@vitesse/base-ui/i18n';
import { LocaleController } from '@vitesse/base-ui/i18n';
import '@vitesse/base-ui/elements/vitesse-icon';
import '@vitesse/base-ui/elements/vitesse-sort-cycle';
import type { SortMode } from '@vitesse/base-ui/elements/vitesse-sort-cycle';

// The tree's sort cycle. FIRST (resting) mode is the NATURAL gateway hierarchy —
// sorting is opt-in, so a bare tree looks unchanged until you cycle. Then the
// whole tree regroups GLOBALLY by type (all PLCs & CNCs, then Models, then
// Gateways, then Integrations), names A→Z within each; the reversed mode flips
// the type-group order. Entities keep their indentation (nested under the type).
const TREE_SORT_MODES: SortMode[] = [
  { key: 'natural', dir: 'asc', icon: 'arrow-down-wide-narrow', label: 'Default order' },
  { key: 'type', dir: 'asc', icon: 'arrow-down-narrow-wide', label: 'Grouped by type' },
  { key: 'type', dir: 'desc', icon: 'arrow-up-wide-narrow', label: 'Grouped by type · reversed' },
];
// Preferred type-group order (the 'type desc' mode reverses it).
const TYPE_ORDER: EntityType[] = ['controller', 'model', 'dashboard', 'gateway', 'integration', 'camera'];

// Hover dwell before we fire a prefetch. Tuned for a POWER user — just long
// enough to skip the few ms of a mouse flying past en route elsewhere, not so
// long it misses a quick deliberate hover. The prefetch is cheap + deduped
// (8 s TTL), so erring short is fine — a stray prefetch costs one indexer query.
const HOVER_DWELL_MS = 30;

/*
 * <vitesse-tree> — React→Lit port of the agent's TreeNavigator. LIGHT DOM so the
 * agent's real `.tree-*` SCSS styles it identically. MULTI-GATEWAY: renders the
 * Workspace group (the org-wide pages), then a gateway root PER org gateway (every entry — even a
 * disconnected one shows, just with no children), each with its controllers/
 * models nested. Pure renderer of `gateways` (the shell owns the managers +
 * re-renders on change); navigation is events.
 */
export type EntityType = 'workspace' | 'canvas' | 'findings' | 'gateway' | 'controller' | 'model' | 'integration' | 'camera' | 'dashboard';

/** What a `tree-select` reports in SELECT mode (see `selectable`). */
export interface TreeSelectDetail {
  entityType: EntityType;
  id: string;
  gatewayId: string;
  label: string;
  nodeId: string;
}

const ENTITY_ICON: Record<EntityType, string> = {
  workspace: 'building-2', // Workspace — the group over the org-wide pages
  canvas: 'waypoints', // Flows — the org data-flow canvas
  findings: 'lightbulb', // Findings — the org's findings log
  gateway: 'hard-drive',
  controller: 'cpu',
  model: 'git-compare-arrows',
  integration: 'database-arrow-up',
  camera: 'camera',
  dashboard: 'bar-chart-3',
};

interface TreeNode {
  id: string;
  label: string;
  entityType: EntityType;
  navId: string; // controllerId/cameraId/modelId/integrationId for leaves, gatewayId for a gateway root
  view: 'canvas' | 'findings' | 'gateway' | 'controller' | 'camera' | 'model' | 'integration' | 'dashboard';
  gatewayId: string; // the OWNING gateway — drives nav + prefetch ('' for canvas)
  children?: TreeNode[];
  separator?: boolean;
  count?: number;
  isGroup?: boolean; // a type-group header (sorted view) — expands, doesn't navigate
  // Entity-leaf metadata (controllers) — preloaded with the controller set, so
  // the tree shows live connection + discovery + symbol-count without a fetch.
  status?: ConnectionStatus;
  discoveryStatus?: DiscoveryStatus;
  symbolCount?: number;
}

@customElement('vitesse-tree')
export class VitesseTree extends LitElement {
  /** The org's gateways (roster + live managers). The shell hands a fresh array
   *  ref on any manager change, so reading `entry.controllers.all` at render time
   *  is always current — no listeners needed here. */
  @property({ attribute: false }) gateways: GatewayEntry[] = [];
  @property({ attribute: false }) backend?: BackendClient;
  @property() selectedId = '';
  /** SELECT mode — the same tree, reused as a picker. A node click emits
   *  `tree-select` instead of navigating; Workspace and the sort cycle are dropped (a
   *  chooser wants to explore, not re-order). Everything else — the hierarchy,
   *  the .tree-* styling, expand/collapse — is inherited as-is. */
  @property({ type: Boolean }) selectable = false;
  /** In select mode, which entity types a click SELECTS. Anything else toggles its
   *  children instead, so a gateway row still expands rather than dead-clicking. */
  @property({ attribute: false }) selectableTypes: EntityType[] = [];
  @state() private expanded = new Set<string>();
  @state() private sortKey = 'natural';
  @state() private sortDir: 'asc' | 'desc' = 'asc';
  private hoverTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    super();
    new LocaleController(this); // re-render on language change
  }

  // Light DOM → agent global SCSS applies to the .tree-* classes.
  protected createRenderRoot(): HTMLElement {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    // This IS the app's navigation, and saying so is what keeps it out of
    // screen reads: "what is on my screen" skips landmarks by role, because
    // chrome that appears on every page costs tokens on every call and
    // distinguishes nothing - and worse, it listed every section of the app the
    // reader had NOT navigated to as though it were in front of them. A real
    // assistive reader gets the same benefit from the same attribute.
    this.setAttribute('role', 'navigation');
    this.setAttribute('aria-label', t('header.toggleTree'));
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.cancelHoverPrefetch();
  }

  // Default-expanded (agent behavior): a node is expanded unless explicitly collapsed.
  private isExpanded(id: string): boolean {
    return !this.expanded.has(`collapsed:${id}`);
  }
  private toggle(id: string): void {
    const k = `collapsed:${id}`;
    const next = new Set(this.expanded);
    next.has(k) ? next.delete(k) : next.add(k);
    this.expanded = next;
  }

  private nav(node: TreeNode): void {
    // SELECT mode: report the pick; a row the consumer can't select (a gateway in a
    // symbol picker) expands instead of dead-clicking.
    if (this.selectable) {
      if (this.selectableTypes.length && !this.selectableTypes.includes(node.entityType)) {
        if (node.children?.length) this.toggle(node.id);
        return;
      }
      const detail: TreeSelectDetail = {
        entityType: node.entityType,
        id: node.navId,
        gatewayId: node.gatewayId,
        label: node.label,
        nodeId: node.id,
      };
      this.dispatchEvent(new CustomEvent('tree-select', { detail, bubbles: true, composed: true }));
      return;
    }
    const detail =
      node.view === 'controller'
        ? { view: node.view, gatewayId: node.gatewayId, controllerId: node.navId }
        : node.view === 'camera'
          ? { view: node.view, gatewayId: node.gatewayId, cameraId: node.navId }
          : node.view === 'model'
          ? { view: node.view, gatewayId: node.gatewayId, modelId: node.navId }
          : node.view === 'integration'
            ? { view: node.view, gatewayId: node.gatewayId, integrationId: node.navId }
            : node.view === 'dashboard'
              ? { view: node.view, gatewayId: node.gatewayId, dashboardId: node.navId }
            : node.view === 'canvas' || node.view === 'findings'
              ? { view: node.view } // org-scoped — no gateway
              : { view: node.view, gatewayId: node.gatewayId };
    this.dispatchEvent(new CustomEvent('navigate', { detail, bubbles: true, composed: true }));
  }

  // Optimistic prefetch: a deliberate hover warms the target's data so the
  // click-through paints instantly — a controller's first symbol page (scoped to
  // its gateway), or a model's SCXML source (via its gateway's model manager).
  // Each reuses the SAME entry point the cards use. A short dwell skips
  // pass-through movement; leave (or navigating elsewhere) cancels it.
  private startHoverPrefetch(node: TreeNode): void {
    this.cancelHoverPrefetch();
    if (node.view === 'controller' && this.backend) {
      const backend = this.backend;
      this.hoverTimer = setTimeout(() => prefetchFirstPage(node.gatewayId, node.navId, backend), HOVER_DWELL_MS);
    } else if (node.view === 'dashboard') {
      const dashboards = this.gateways.find((e) => e.gateway.deviceFingerprint === node.gatewayId)?.dashboards;
      if (dashboards) this.hoverTimer = setTimeout(() => dashboards.prefetchBody(node.navId), HOVER_DWELL_MS);
    } else if (node.view === 'model') {
      const models = this.gateways.find((e) => e.gateway.deviceFingerprint === node.gatewayId)?.models;
      if (models) this.hoverTimer = setTimeout(() => models.prefetchSource(node.navId), HOVER_DWELL_MS);
    }
  }
  private cancelHoverPrefetch(): void {
    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = undefined;
    }
  }

  /** One gateway root node with its controllers + models nested (read live from
   *  the entry's managers). A gateway with nothing/no connection still renders —
   *  just with no children. */
  private buildGatewayNode(e: GatewayEntry): TreeNode {
    const gw = e.gateway.deviceFingerprint;
    const groups: TreeNode[][] = [
      e.controllers.all.map((c) => ({
        id: `controller-${gw}-${c.id}`,
        label: c.name,
        entityType: 'controller' as const,
        navId: c.id,
        view: 'controller' as const,
        gatewayId: gw,
        status: c.status,
        discoveryStatus: c.discoveryStatus,
        symbolCount: c.symbolCount,
      })),
      e.cameras.all.map((c) => ({
        id: `camera-${gw}-${c.id}`,
        label: c.name,
        entityType: 'camera' as const,
        navId: c.id,
        view: 'camera' as const,
        gatewayId: gw,
      })),
      e.models.all.map((m) => ({
        id: `model-${gw}-${m.id}`,
        label: m.name,
        entityType: 'model' as const,
        navId: m.id,
        view: 'model' as const,
        gatewayId: gw,
      })),
      e.integrations.all.map((i) => ({
        id: `integration-${gw}-${i.id}`,
        label: i.displayName,
        entityType: 'integration' as const,
        navId: i.id,
        view: 'integration' as const,
        gatewayId: gw,
      })),
      e.dashboards.all.map((d) => ({
        id: `dashboard-${gw}-${d.id}`,
        label: d.title,
        entityType: 'dashboard' as const,
        navId: d.id,
        view: 'dashboard' as const,
        gatewayId: gw,
      })),
    ];
    // Sub-entities only when we're attached — a disconnected/connecting gateway
    // node has no children (same as the home grid hiding their cards).
    const children: TreeNode[] = [];
    if (entryConnState(e) === 'connected') {
      for (const g of groups) {
        if (g.length === 0) continue;
        g[0].separator = children.length > 0;
        children.push(...g);
      }
    }
    return {
      id: `gateway-${gw}`,
      label: gatewayLabel(e.gateway),
      entityType: 'gateway',
      navId: gw,
      view: 'gateway',
      gatewayId: gw,
      children,
      count: children.length,
    };
  }

  /** The GLOBAL type-grouped view (sorted modes): every entity across every
   *  gateway, bucketed by type into expandable type-group headers (PLCs & CNCs,
   *  Models, Gateways, Integrations, Cameras), the groups in the preferred order
   *  (reversed for 'desc'), names A→Z within each. Entities keep their indentation
   *  by nesting under the type header — same depth they had under a gateway. */
  private buildTypeGroupedRoots(): TreeNode[] {
    const buckets: Record<string, TreeNode[]> = { controller: [], model: [], dashboard: [], camera: [], integration: [], gateway: [] };
    for (const e of this.gateways) {
      const gw = e.gateway.deviceFingerprint;
      if (entryConnState(e) === 'connected') {
        for (const c of e.controllers.all)
          buckets.controller.push({ id: `controller-${gw}-${c.id}`, label: c.name, entityType: 'controller', navId: c.id, view: 'controller', gatewayId: gw, status: c.status, discoveryStatus: c.discoveryStatus, symbolCount: c.symbolCount });
        for (const m of e.models.all) buckets.model.push({ id: `model-${gw}-${m.id}`, label: m.name, entityType: 'model', navId: m.id, view: 'model', gatewayId: gw });
        for (const c of e.cameras.all) buckets.camera.push({ id: `camera-${gw}-${c.id}`, label: c.name, entityType: 'camera', navId: c.id, view: 'camera', gatewayId: gw });
        for (const d of e.dashboards.all) buckets.dashboard.push({ id: `dashboard-${gw}-${d.id}`, label: d.title, entityType: 'dashboard', navId: d.id, view: 'dashboard', gatewayId: gw });
        for (const i of e.integrations.all) buckets.integration.push({ id: `integration-${gw}-${i.id}`, label: i.displayName, entityType: 'integration', navId: i.id, view: 'integration', gatewayId: gw });
      }
      buckets.gateway.push({ id: `gateway-${gw}`, label: gatewayLabel(e.gateway), entityType: 'gateway', navId: gw, view: 'gateway', gatewayId: gw });
    }
    const labelFor: Record<string, string> = { controller: t('model.controllers'), model: t('model.models'), gateway: 'Relays', integration: 'Integrations', camera: 'Cameras' };
    const order = this.sortDir === 'desc' ? [...TYPE_ORDER].reverse() : TYPE_ORDER;
    const roots: TreeNode[] = [];
    for (const type of order) {
      const nodes = buckets[type];
      if (!nodes?.length) continue;
      nodes.sort((a, b) => a.label.localeCompare(b.label));
      roots.push({ id: `typegroup-${type}`, label: labelFor[type] ?? type, entityType: type, navId: '', view: 'gateway', gatewayId: '', children: nodes, count: nodes.length, isGroup: true });
    }
    return roots;
  }

  private renderNode(node: TreeNode, depth = 0): unknown {
    const hasChildren = !!node.children && node.children.length > 0;
    const expanded = hasChildren ? this.isExpanded(node.id) : false;
    // Match on the node's composite id (encodes the owning gateway), NOT navId —
    // a bare entity id collides across gateways (same model on two gateways), and
    // a model runs in exactly one gateway instance, so selection is per-instance.
    const active = this.selectedId === node.id;
    return html`
      <div class="tree-node-wrapper">
        ${node.separator ? html`<div class="tree-separator" style="margin-left:${depth * 16 + 24}px"></div>` : nothing}
        <div class="tree-node" style="padding-left:${depth * 16}px">
          ${hasChildren
            ? html`<button
                class="tree-chevron ${expanded ? 'expanded' : 'collapsed'}"
                @click=${(e: Event) => {
                  e.stopPropagation();
                  this.toggle(node.id);
                }}
                aria-label=${expanded ? t('common.collapse') : t('common.expand')}
              >
                <vitesse-icon name="chevron-right" size="14"></vitesse-icon>
              </button>`
            : // A leaf keeps the spacer so it aligns under its siblings, and an empty
              // gateway keeps it so gaining a chevron later doesn't shift the row.
              html`<span class="tree-chevron-spacer"></span>`}
          <button
            class="tree-label ${active ? 'active' : ''}"
            @click=${() => (node.isGroup ? this.toggle(node.id) : this.nav(node))}
            @mouseenter=${() => this.startHoverPrefetch(node)}
            @mouseleave=${() => this.cancelHoverPrefetch()}
          >
            <!-- Every node (incl. controllers) shows its entity icon — no status
                 dot, symbol count, or scanning label in the tree; that detail
                 lives on the controller cards / detail page, not the nav. -->
            <span class="tree-icon"><vitesse-icon name=${ENTITY_ICON[node.entityType]} size="14"></vitesse-icon></span>
            <span class="tree-text">${node.label}</span>
          </button>
          <!-- The sort cycle rides the Workspace row, the tree's first, pushed to the
               right (level with it), so the tree-row hover never reaches it. -->
          ${node.entityType === 'workspace' && !this.selectable
            ? html`<vitesse-sort-cycle
                class="tree-sort"
                .modes=${TREE_SORT_MODES}
                @sort-change=${(e: CustomEvent<{ key: string; dir: 'asc' | 'desc' }>) => {
                  this.sortKey = e.detail.key;
                  this.sortDir = e.detail.dir;
                }}
              ></vitesse-sort-cycle>`
            : nothing}
        </div>
        ${hasChildren
          ? html`<div class="tree-children-wrapper" style="grid-template-rows:${expanded ? '1fr' : '0fr'};opacity:${expanded ? 1 : 0}">
              <div class="tree-children-overflow">${node.children!.map((c) => this.renderNode(c, depth + 1))}</div>
            </div>`
          : nothing}
      </div>
    `;
  }

  // WORKSPACE — the group over the pages that span every relay, above the gateways. A page that is
  // org-wide goes in here rather than to the top of the tree, so the tree's top stays one group and
  // the relays however many pages are added. A header only: it expands, it has no page of its own.
  private workspaceNode(): TreeNode {
    return {
      id: 'workspace',
      label: t('workspace.title'),
      entityType: 'workspace',
      navId: '',
      view: 'gateway',
      gatewayId: '',
      isGroup: true,
      children: [this.canvasNode(), this.findingsNode()],
    };
  }
  // The org-wide program canvas (org-scoped, not under any one gateway).
  private canvasNode(): TreeNode {
    return { id: 'canvas', label: t('canvas.title'), entityType: 'canvas', navId: 'canvas', view: 'canvas', gatewayId: '' };
  }
  // The org's findings log — org-scoped for the same reason: what waits on a person spans every relay.
  private findingsNode(): TreeNode {
    return { id: 'findings', label: t('findings.title'), entityType: 'findings', navId: 'findings', view: 'findings', gatewayId: '' };
  }

  render() {
    // Sorted modes regroup the WHOLE tree globally by type; natural keeps the
    // per-gateway hierarchy. Workspace stays pinned at the top either way.
    const roots =
      this.sortKey === 'natural' ? this.gateways.map((e) => this.buildGatewayNode(e)) : this.buildTypeGroupedRoots();
    return html`<div class="tree-navigator">
      ${this.styles()}
      <div class="tree-content">
        ${this.selectable ? nothing : this.renderNode(this.workspaceNode())}
        ${roots.map((n) => this.renderNode(n))}
      </div>
    </div>`;
  }

  private styles() {
    return html`<style>
      /* the sort cycle rides the Workspace row, pushed to the right — the tree-node is
         a flex row so margin-left:auto lands it level with the label, clear of the
         row-hover to its left */
      .tree-navigator .tree-node .tree-sort {
        margin-left: auto;
        flex-shrink: 0;
      }
    </style>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-tree': VitesseTree;
  }
}
